import React, { useState, useEffect, useCallback } from "react";

const C = {
  bg:"#06080F", surface:"#0e1117", card:"#141820", border:"#1e2533",
  accent:"#F59E0B", accentDim:"#F59E0B22", text:"#F1F5F9", muted:"#64748B",
  danger:"#EF4444", success:"#22C55E", info:"#3B82F6", purple:"#8B5CF6",
};

function api(path, opts = {}) {
  return fetch(path, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  }).then((r) => r.json());
}

function fmt(n, decimals = 0) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

function fmtRupee(n) {
  if (n == null) return "—";
  return `₹${fmt(n)}`;
}

function fmtMargin(n) {
  if (n == null) return "—";
  return `${n}%`;
}

function monthLabel(m) {
  const [y, mo] = m.split("-");
  return new Date(parseInt(y), parseInt(mo) - 1, 1).toLocaleDateString("en-IN", {
    month: "short", year: "2-digit",
  });
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: "18px 20px", flex: "1 1 160px", minWidth: 140,
    }}>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ color: color || C.text, fontSize: 22, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: 1,
      textTransform: "uppercase", margin: "24px 0 10px" }}>
      {children}
    </div>
  );
}

function Table({ cols, rows, emptyMsg = "No data", onRowClick }) {
  if (!rows || rows.length === 0) {
    return <div style={{ color: C.muted, fontSize: 13, padding: "16px 0" }}>{emptyMsg}</div>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.key} style={{
                textAlign: c.align || "left", padding: "8px 12px",
                color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`,
                whiteSpace: "nowrap",
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                borderBottom: `1px solid ${C.border}22`,
                cursor: onRowClick ? "pointer" : "default",
              }}
              onMouseEnter={onRowClick ? (e) => e.currentTarget.style.background = C.surface : undefined}
              onMouseLeave={onRowClick ? (e) => e.currentTarget.style.background = "transparent" : undefined}
            >
              {cols.map((c) => (
                <td key={c.key} style={{
                  padding: "8px 12px", color: C.text, textAlign: c.align || "left",
                }}>
                  {c.render ? c.render(row) : row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Shared form styles for expense UI ─────────────────────────────────────────

const inputSt = {
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
  color: C.text, padding: "6px 10px", fontSize: 13, width: "100%", boxSizing: "border-box",
};
const primaryBtn = {
  background: C.accent, color: "#000", border: "none", borderRadius: 6,
  padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const ghostBtn = {
  background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
  borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer",
};

const EXPENSE_CATEGORIES = [
  "API Costs", "Server / Hosting", "Marketing", "Salaries", "Contractor",
  "Travel", "Office", "Legal", "Accounting", "Software", "Other",
];

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: C.muted, fontSize: 11, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function ModalOverlay({ children, onClose, wide }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
          padding: 24, width: wide ? 680 : 420, maxWidth: "100%",
          maxHeight: "90vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function expenseStatusChip(status) {
  const map = {
    unpaid:  { bg: C.danger  + "22", color: C.danger,  label: "Unpaid"  },
    partial: { bg: C.accent  + "22", color: C.accent,  label: "Partial" },
    paid:    { bg: C.success + "22", color: C.success, label: "Paid"    },
    void:    { bg: C.muted   + "22", color: C.muted,   label: "Void"    },
  };
  const s = map[status] || map.unpaid;
  return (
    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4,
      background: s.bg, color: s.color, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function paidTotal(expense) {
  return (expense.payments || []).reduce((s, p) => s + p.amount, 0);
}

function downloadAttachment(att) {
  const token = localStorage.getItem("console_token");
  fetch(`/api/admin/attachments/${att.id}/file`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then((r) => r.blob())
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement("a");
      a.href     = url;
      a.download = att.originalName;
      a.click();
      URL.revokeObjectURL(url);
    });
}

// ── New Expense Modal ─────────────────────────────────────────────────────────

function NewExpenseModal({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm]     = useState({ category: "", vendor: "", amount: "", currency: "INR", description: "", incurredDate: today, notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function submit(e) {
    e.preventDefault();
    if (!form.category)   { setErr("Category is required."); return; }
    if (!form.amount || isNaN(parseFloat(form.amount)) || parseFloat(form.amount) <= 0) { setErr("A valid positive amount is required."); return; }
    setSaving(true); setErr(null);
    api("/api/admin/expenses", { method: "POST", body: JSON.stringify(form) })
      .then((d) => { if (d.error) { setErr(d.error); setSaving(false); } else onSaved(d); })
      .catch((e) => { setErr(e.message); setSaving(false); });
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Log New Expense</div>
      {err && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      <form onSubmit={submit}>
        <FormField label="Category *">
          <select value={form.category} onChange={(e) => set("category", e.target.value)} style={inputSt} required>
            <option value="">Select category…</option>
            {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Vendor / Supplier">
          <input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="e.g. Google Cloud, ElevenLabs" style={inputSt} />
        </FormField>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 2 }}>
            <FormField label="Amount *">
              <input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" step="0.01" min="0.01" style={inputSt} required />
            </FormField>
          </div>
          <div style={{ flex: 1 }}>
            <FormField label="Currency">
              <select value={form.currency} onChange={(e) => set("currency", e.target.value)} style={inputSt}>
                <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option>
              </select>
            </FormField>
          </div>
        </div>
        <FormField label="Date Incurred *">
          <input type="date" value={form.incurredDate} onChange={(e) => set("incurredDate", e.target.value)} style={inputSt} required />
        </FormField>
        <FormField label="Description">
          <input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Brief description" style={inputSt} />
        </FormField>
        <FormField label="Notes (internal)">
          <input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional" style={inputSt} />
        </FormField>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
          <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
          <button type="submit" disabled={saving} style={primaryBtn}>{saving ? "Saving…" : "Log Expense"}</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Expense Detail Panel ──────────────────────────────────────────────────────

function ExpenseDetail({ expense: init, accounts, onClose, onListRefresh }) {
  const [expense,     setExpense]     = useState(init);
  const [attachments, setAttachments] = useState(init.attachments || []);
  const [showPay,     setShowPay]     = useState(false);
  const [payForm,     setPayForm]     = useState({ accountId: "", amount: "", paymentDate: new Date().toISOString().slice(0, 10), method: "", reference: "", notes: "" });
  const [uploading,   setUploading]   = useState(false);
  const [savingPay,   setSavingPay]   = useState(false);
  const [err,         setErr]         = useState(null);

  function setPay(k, v) { setPayForm((f) => ({ ...f, [k]: v })); }

  function reload() {
    api(`/api/admin/expenses/${expense.id}`).then((d) => {
      if (!d.error) { setExpense(d); setAttachments(d.attachments || []); }
    });
  }

  function submitPayment(e) {
    e.preventDefault();
    if (!payForm.accountId) { setErr("Account is required."); return; }
    if (!payForm.amount || isNaN(parseFloat(payForm.amount)) || parseFloat(payForm.amount) <= 0) { setErr("Valid positive amount required."); return; }
    setSavingPay(true); setErr(null);
    api(`/api/admin/expenses/${expense.id}/payments`, { method: "POST", body: JSON.stringify(payForm) })
      .then((d) => {
        setSavingPay(false);
        if (d.error) { setErr(d.error); return; }
        setShowPay(false);
        setPayForm({ accountId: "", amount: "", paymentDate: new Date().toISOString().slice(0, 10), method: "", reference: "", notes: "" });
        reload(); onListRefresh();
      })
      .catch((e) => { setErr(e.message); setSavingPay(false); });
  }

  function deletePayment(paymentId) {
    if (!window.confirm("Remove this payment?")) return;
    api(`/api/admin/expenses/${expense.id}/payments/${paymentId}`, { method: "DELETE" })
      .then(() => { reload(); onListRefresh(); });
  }

  function uploadFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setErr(null);
    const fd  = new FormData();
    fd.append("file", file);
    fd.append("entityType", "expense");
    fd.append("entityId",   expense.id);
    const token = localStorage.getItem("console_token");
    fetch("/api/admin/attachments", {
      method:  "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body:    fd,
    })
      .then((r) => r.json())
      .then((d) => {
        setUploading(false);
        if (d.error) { setErr(d.error); return; }
        setAttachments((prev) => [...prev, d]);
      })
      .catch((e) => { setUploading(false); setErr(e.message); });
    e.target.value = "";
  }

  function deleteAttachment(attId) {
    if (!window.confirm("Delete this attachment?")) return;
    api(`/api/admin/attachments/${attId}`, { method: "DELETE" })
      .then(() => setAttachments((prev) => prev.filter((a) => a.id !== attId)));
  }

  const paid      = paidTotal(expense);
  const balance   = expense.amount - paid;
  const payments  = expense.payments || [];

  return (
    <ModalOverlay onClose={onClose} wide>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 17 }}>
            {expense.vendor || expense.category}
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
            {expense.category} · {expense.incurredDate?.slice(0, 10)} · {expenseStatusChip(expense.status)}
          </div>
          {expense.description && (
            <div style={{ color: C.muted, fontSize: 13, marginTop: 6 }}>{expense.description}</div>
          )}
        </div>
        <button onClick={onClose} style={{ ...ghostBtn, padding: "4px 10px" }}>✕</button>
      </div>

      {err && <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{err}</div>}

      {/* Summary */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
        <StatCard label="Obligated" value={fmtRupee(expense.amount)} color={C.danger} />
        <StatCard label="Paid"      value={fmtRupee(paid)}          color={C.success} />
        <StatCard label="Balance"   value={fmtRupee(balance)}       color={balance > 0 ? C.accent : C.muted} />
      </div>

      {/* Payments */}
      <SectionHeading>Payments</SectionHeading>
      {payments.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13, marginBottom: 8 }}>No payments recorded yet.</div>
      ) : (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse", marginBottom: 8 }}>
          <thead>
            <tr>
              {["Date", "Account", "Method", "Reference", "Amount", ""].map((h) => (
                <th key={h} style={{ textAlign: h === "Amount" ? "right" : "left", padding: "4px 8px", color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}22` }}>
                <td style={{ padding: "6px 8px" }}>{p.paymentDate?.slice(0, 10)}</td>
                <td style={{ padding: "6px 8px" }}>{p.account?.name || "—"}</td>
                <td style={{ padding: "6px 8px", color: C.muted }}>{p.method || "—"}</td>
                <td style={{ padding: "6px 8px", color: C.muted, fontSize: 12 }}>{p.reference || "—"}</td>
                <td style={{ padding: "6px 8px", textAlign: "right", color: C.success, fontWeight: 600 }}>{fmtRupee(p.amount)}</td>
                <td style={{ padding: "6px 4px" }}>
                  <button onClick={() => deletePayment(p.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 13 }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!showPay ? (
        <button onClick={() => setShowPay(true)} style={{ ...ghostBtn, fontSize: 12, marginBottom: 20 }}>+ Add Payment</button>
      ) : (
        <form onSubmit={submitPayment} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 20 }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Record Payment</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <div style={{ flex: "1 1 160px" }}>
              <FormField label="Account *">
                <select value={payForm.accountId} onChange={(e) => setPay("accountId", e.target.value)} style={inputSt} required>
                  <option value="">Select…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type})</option>)}
                </select>
              </FormField>
            </div>
            <div style={{ flex: "1 1 110px" }}>
              <FormField label="Amount *">
                <input type="number" value={payForm.amount} onChange={(e) => setPay("amount", e.target.value)} step="0.01" min="0.01" style={inputSt} required />
              </FormField>
            </div>
            <div style={{ flex: "1 1 130px" }}>
              <FormField label="Payment Date *">
                <input type="date" value={payForm.paymentDate} onChange={(e) => setPay("paymentDate", e.target.value)} style={inputSt} required />
              </FormField>
            </div>
            <div style={{ flex: "1 1 130px" }}>
              <FormField label="Method">
                <select value={payForm.method} onChange={(e) => setPay("method", e.target.value)} style={inputSt}>
                  <option value="">—</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="other">Other</option>
                </select>
              </FormField>
            </div>
            <div style={{ flex: "1 1 160px" }}>
              <FormField label="Reference (UTR / txn ID)">
                <input value={payForm.reference} onChange={(e) => setPay("reference", e.target.value)} placeholder="Optional" style={inputSt} />
              </FormField>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={() => setShowPay(false)} style={ghostBtn}>Cancel</button>
            <button type="submit" disabled={savingPay} style={primaryBtn}>{savingPay ? "Saving…" : "Record Payment"}</button>
          </div>
        </form>
      )}

      {/* Attachments */}
      <SectionHeading>Receipts &amp; Attachments</SectionHeading>
      {attachments.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13, marginBottom: 8 }}>No attachments yet.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          {attachments.map((att) => (
            <div key={att.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16 }}>{att.mimeType === "application/pdf" ? "📄" : "🖼️"}</span>
              <button onClick={() => downloadAttachment(att)} style={{ background: "none", border: "none", color: C.info, cursor: "pointer", fontSize: 13, padding: 0, textDecoration: "underline" }}>
                {att.originalName}
              </button>
              <span style={{ color: C.muted, fontSize: 11 }}>({Math.round(att.sizeBytes / 1024)} KB)</span>
              <button onClick={() => deleteAttachment(att.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 12 }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <label style={{ display: "inline-block", cursor: "pointer" }}>
        <span style={{ ...ghostBtn, fontSize: 12, display: "inline-block" }}>
          {uploading ? "Uploading…" : "📎 Upload Receipt"}
        </span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" onChange={uploadFile} style={{ display: "none" }} disabled={uploading} />
      </label>
    </ModalOverlay>
  );
}

// ── Expenses Tab ──────────────────────────────────────────────────────────────

function ExpensesTab({ ctx }) {
  const [data,     setData]     = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [err,      setErr]      = useState(null);
  const [selected, setSelected] = useState(null);
  const [showNew,  setShowNew]  = useState(false);

  const load = useCallback(() => {
    setErr(null);
    const qs = `from=${ctx.from}&to=${ctx.to}`;
    Promise.all([
      api(`/api/admin/expenses?${qs}`),
      api("/api/admin/accounts"),
    ]).then(([d, accts]) => {
      if (d.error)     { setErr(d.error);     return; }
      if (accts.error) { setErr(accts.error); return; }
      setData(d);
      setAccounts(accts.accounts || []);
    }).catch((e) => setErr(e.message));
  }, [ctx.from, ctx.to]);

  useEffect(() => { setData(null); setSelected(null); load(); }, [load]);

  if (err)   return <ErrMsg msg={err} />;
  if (!data) return <Loader />;

  const expenses        = data.expenses || [];
  const totalObligation = expenses.reduce((s, e) => s + e.amount,    0);
  const totalPaid       = expenses.reduce((s, e) => s + paidTotal(e), 0);
  const outstanding     = totalObligation - totalPaid;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div style={{ color: C.text, fontWeight: 600 }}>
          Expenses — {ctx.from} to {ctx.to}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowNew(true)} style={primaryBtn}>+ Log Expense</button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <StatCard label="Obligated"   value={fmtRupee(totalObligation)} sub={`${expenses.length} expenses`} color={C.danger}  />
        <StatCard label="Paid"        value={fmtRupee(totalPaid)}        color={C.success} />
        <StatCard label="Outstanding" value={fmtRupee(outstanding)}      color={outstanding > 0 ? C.accent : C.muted} />
      </div>

      <Table
        cols={[
          { key: "incurredDate", label: "Date",       render: (r) => r.incurredDate?.slice(0, 10) },
          { key: "vendor",       label: "Vendor",     render: (r) => r.vendor || <span style={{ color: C.muted }}>—</span> },
          { key: "category",     label: "Category" },
          { key: "amount",       label: "Obligated",  align: "right", render: (r) => fmtRupee(r.amount) },
          { key: "paid",         label: "Paid",       align: "right", render: (r) => fmtRupee(paidTotal(r)) },
          { key: "status",       label: "Status",     render: (r) => expenseStatusChip(r.status) },
        ]}
        rows={expenses}
        emptyMsg="No expenses in this period."
        onRowClick={(r) => setSelected(r)}
      />

      {accounts.length === 0 && (
        <div style={{ marginTop: 16, padding: "10px 14px", background: C.accentDim, borderRadius: 8, fontSize: 13, color: C.accent }}>
          No payment accounts set up yet. Add one via the Accounts API before recording payments.
        </div>
      )}

      {showNew && (
        <NewExpenseModal
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}

      {selected && (
        <ExpenseDetail
          expense={selected}
          accounts={accounts}
          onClose={() => setSelected(null)}
          onListRefresh={load}
        />
      )}
    </div>
  );
}

function Loader() {
  return <div style={{ color: C.muted, padding: 32, textAlign: "center" }}>Loading…</div>;
}

function ErrMsg({ msg }) {
  return <div style={{ color: C.danger, padding: 16, fontSize: 13 }}>{msg}</div>;
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

function OverviewTab({ ctx }) {
  const [data,   setData]   = useState(null);
  const [liab,   setLiab]   = useState(null);
  const [disb,   setDisb]   = useState(null);
  const [ttc,    setTtc]    = useState(null);
  const [err,    setErr]    = useState(null);

  useEffect(() => {
    setData(null); setLiab(null); setDisb(null); setTtc(null); setErr(null);
    const qs = `from=${ctx.from}&to=${ctx.to}&basis=${ctx.basis}`;
    Promise.all([
      api(`/api/admin/finance/summary?${qs}`),
      api(`/api/admin/finance/payouts/liability`),
      api(`/api/admin/finance/payouts/disbursed?${qs}`),
      api(`/api/admin/finance/time-to-cash?${qs}`),
    ]).then(([s, l, d, t]) => {
      if (s.error || l.error || d.error || t.error) {
        setErr(s.error || l.error || d.error || t.error); return;
      }
      setData(s); setLiab(l); setDisb(d); setTtc(t);
    }).catch((e) => setErr(e.message));
  }, [ctx.from, ctx.to, ctx.basis]);

  if (err)   return <ErrMsg msg={err} />;
  if (!data) return <Loader />;

  const profitColor = data.grossProfit >= 0 ? C.success : C.danger;
  // Confirmed liability = approved obligations only (pending_payout + on_hold)
  const confirmedLiability = liab ? (liab.pendingPayout || 0) + (liab.onHold || 0) : null;

  return (
    <div>
      <SectionHeading>P&L — {ctx.from} to {ctx.to} ({ctx.basis})</SectionHeading>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard label="Revenue"      value={fmtRupee(data.totalRevenue)} sub={`${data.revenueCount} txns`} color={C.success} />
        <StatCard label="Expenses"     value={fmtRupee(data.totalExpense)} sub={`${data.expenseCount} txns`} color={C.danger}  />
        <StatCard label="Gross Profit" value={fmtRupee(data.grossProfit)}  color={profitColor} />
        <StatCard label="Gross Margin" value={fmtMargin(data.grossMargin)} color={profitColor} />
      </div>

      <SectionHeading>Payout Liability (current snapshot)</SectionHeading>
      {liab && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <StatCard label="Contingent (Under Review)" value={fmtRupee(liab.underReview)}   color={C.accent} sub="Not yet approved" />
          <StatCard label="Pending Payout"            value={fmtRupee(liab.pendingPayout)} color={C.info}   />
          <StatCard label="On Hold"                   value={fmtRupee(liab.onHold)}        color={C.muted}  />
          <StatCard label="Confirmed Liability"       value={fmtRupee(confirmedLiability)} color={C.danger} sub="Approved + On Hold" />
          <StatCard label="Potential Liability"       value={fmtRupee(liab.total)}         color={C.muted}  sub="All outstanding" />
        </div>
      )}

      <SectionHeading>Payout Disbursements — {ctx.from} to {ctx.to}</SectionHeading>
      {disb && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <StatCard label="Paid This Period"  value={fmtRupee(disb.totalPaid)}                                                                                      color={C.success} sub={`${disb.count} payouts`} />
          <StatCard label="Survey Payouts"    value={fmtRupee(disb.byType?.survey || 0)}                                                                             color={C.info}    />
          <StatCard label="Referral Rewards"  value={fmtRupee((disb.byType?.referral_referrer_1 || 0) + (disb.byType?.referral_referrer_2 || 0))}                    color={C.purple}  />
          <StatCard label="Referral Bonuses"  value={fmtRupee(disb.byType?.referral_referee_bonus || 0)}                                                             color={C.success} />
        </div>
      )}

      <SectionHeading>Time-to-Cash</SectionHeading>
      {ttc && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <StatCard label="Avg Days" value={ttc.avgDays    != null ? `${ttc.avgDays}d`    : "—"} />
          <StatCard label="Median"   value={ttc.medianDays != null ? `${ttc.medianDays}d` : "—"} />
          <StatCard label="Slowest"  value={ttc.maxDays    != null ? `${ttc.maxDays}d`    : "—"} />
          <StatCard label="Invoices" value={ttc.count} />
        </div>
      )}
    </div>
  );
}

function MonthlyTab({ ctx }) {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api(`/api/admin/finance/monthly?from=${ctx.from}&to=${ctx.to}&basis=${ctx.basis}`)
      .then((d) => { if (d.error) { setErr(d.error); return; } setData(d); })
      .catch((e) => setErr(e.message));
  }, [ctx.from, ctx.to, ctx.basis]);

  if (err)   return <ErrMsg msg={err} />;
  if (!data) return <Loader />;

  const months = data.monthly || [];

  return (
    <div>
      <SectionHeading>Monthly P&L</SectionHeading>
      {months.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13 }}>No transactions in this period.</div>
      ) : (
        <Table
          cols={[
            { key: "month",       label: "Month",   render: (r) => monthLabel(r.month) },
            { key: "revenue",     label: "Revenue",      align: "right", render: (r) => fmtRupee(r.revenue) },
            { key: "expense",     label: "Expenses",     align: "right", render: (r) => fmtRupee(r.expense) },
            { key: "grossProfit", label: "Gross Profit", align: "right", render: (r) => (
              <span style={{ color: r.grossProfit >= 0 ? C.success : C.danger }}>{fmtRupee(r.grossProfit)}</span>
            )},
            { key: "grossMargin", label: "Margin",  align: "right", render: (r) => fmtMargin(r.grossMargin) },
            { key: "transactions",label: "Txns",    align: "right" },
          ]}
          rows={months}
        />
      )}

      <SectionHeading>Summary</SectionHeading>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard label="Revenue"      value={fmtRupee(data.summary?.totalRevenue)} color={C.success} />
        <StatCard label="Expenses"     value={fmtRupee(data.summary?.totalExpense)} color={C.danger}  />
        <StatCard label="Gross Profit" value={fmtRupee(data.summary?.grossProfit)}
          color={data.summary?.grossProfit >= 0 ? C.success : C.danger} />
        <StatCard label="Gross Margin" value={fmtMargin(data.summary?.grossMargin)}
          color={data.summary?.grossProfit >= 0 ? C.success : C.danger} />
      </div>
    </div>
  );
}

function SurveysTab({ ctx }) {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api(`/api/admin/finance/surveys?from=${ctx.from}&to=${ctx.to}`)
      .then((d) => { if (d.error) { setErr(d.error); return; } setData(d); })
      .catch((e) => setErr(e.message));
  }, [ctx.from, ctx.to]);

  if (err)   return <ErrMsg msg={err} />;
  if (!data) return <Loader />;

  const surveys = data.surveys || [];

  return (
    <div>
      <SectionHeading>Survey Economics (published {ctx.from} – {ctx.to})</SectionHeading>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>
        Revenue attribution is heuristic — allocated from customer invoices proportionally by survey completions.
        "Direct" means the customer had exactly one active survey.
      </div>
      <Table
        cols={[
          { key: "title",              label: "Survey",        render: (r) => (
            <span title={r.surveyId}>{r.title}</span>
          )},
          { key: "completions",        label: "Completions",   align: "right" },
          { key: "revenueAttributed",  label: "Revenue ≈",     align: "right", render: (r) => fmtRupee(r.revenueAttributed) },
          { key: "payoutCost",         label: "Payout Cost",   align: "right", render: (r) => fmtRupee(r.payoutCost) },
          { key: "contribution",       label: "Contribution",  align: "right", render: (r) => (
            <span style={{ color: r.contribution >= 0 ? C.success : C.danger }}>
              {fmtRupee(r.contribution)}
            </span>
          )},
          { key: "contributionMargin", label: "Margin",        align: "right", render: (r) => fmtMargin(r.contributionMargin) },
          { key: "isReliable",         label: "Attribution",   render: (r) => (
            <span style={{
              fontSize: 11, padding: "2px 7px", borderRadius: 4,
              background: r.isReliable ? C.success + "22" : C.accent + "22",
              color:      r.isReliable ? C.success : C.accent,
            }}>
              {r.revenueAttributionNote === "direct" ? "Direct" :
               r.revenueAttributionNote === "proportional_by_completions" ? "Proportional" :
               "No invoice"}
            </span>
          )},
        ]}
        rows={surveys}
        emptyMsg="No surveys published in this period."
      />
    </div>
  );
}

function ReferralsTab({ ctx }) {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api(`/api/admin/finance/referrals?from=${ctx.from}&to=${ctx.to}&basis=${ctx.basis}`)
      .then((d) => { if (d.error) { setErr(d.error); return; } setData(d); })
      .catch((e) => setErr(e.message));
  }, [ctx.from, ctx.to, ctx.basis]);

  if (err)   return <ErrMsg msg={err} />;
  if (!data) return <Loader />;

  return (
    <div>
      <SectionHeading>Referral Program Cost ({ctx.from} – {ctx.to})</SectionHeading>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard label="Total Referral Cost"   value={fmtRupee(data.totalReferralCost)}  color={C.danger}  />
        <StatCard label="Referrer Rewards"       value={fmtRupee(data.referrerRewards)}    color={C.accent}  />
        <StatCard label="Referee Bonuses"        value={fmtRupee(data.refereeBonuses)}     color={C.purple}  />
        <StatCard label="Milestone 1 Payouts"   value={data.milestone1Count}              sub="@₹30 each"  />
        <StatCard label="Milestone 2 Payouts"   value={data.milestone2Count}              sub="@₹20 each"  />
        <StatCard label="Avg Cost per Referral" value={fmtRupee(data.avgCostPerReferral)} />
      </div>
      <div style={{ color: C.muted, fontSize: 12, marginTop: 12 }}>
        Basis: <strong style={{ color: C.text }}>{ctx.basis}</strong>.
        Cash = only paid-out referral rewards. Accrual = all non-cancelled referral payouts created in period.
      </div>
    </div>
  );
}

function GSTTab({ ctx }) {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api(`/api/admin/finance/gst?from=${ctx.from}&to=${ctx.to}&basis=${ctx.basis}`)
      .then((d) => { if (d.error) { setErr(d.error); return; } setData(d); })
      .catch((e) => setErr(e.message));
  }, [ctx.from, ctx.to, ctx.basis]);

  if (err)   return <ErrMsg msg={err} />;
  if (!data) return <Loader />;

  return (
    <div>
      <SectionHeading>GST Summary ({ctx.from} – {ctx.to})</SectionHeading>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard label="Taxable Base" value={fmtRupee(data.taxableBase)} />
        <StatCard label="Total Tax"    value={fmtRupee(data.totalTax)}    color={C.accent}  />
        <StatCard label="IGST"         value={fmtRupee(data.igst)}        color={C.info}    />
        <StatCard label="CGST"         value={fmtRupee(data.cgst)}        />
        <StatCard label="SGST"         value={fmtRupee(data.sgst)}        />
      </div>
      <SectionHeading>Invoice Classification</SectionHeading>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard label="B2B Invoices"    value={data.b2bCount}    sub="Valid GSTIN on record" />
        <StatCard label="B2C Invoices"    value={data.b2cCount}    sub="No GSTIN"              />
        <StatCard label="Exempt / 0%GST" value={data.exemptCount} sub="taxRate = 0"           />
      </div>
      <div style={{ color: C.muted, fontSize: 12, marginTop: 12 }}>
        Inter-state detection uses <code>RIK_GSTIN</code> env var (first 2 digits = supplier state).
        B2B = customer GSTIN on invoice passes format check.
      </div>
    </div>
  );
}

function TimeToCashTab({ ctx }) {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api(`/api/admin/finance/time-to-cash?from=${ctx.from}&to=${ctx.to}&basis=${ctx.basis}`)
      .then((d) => { if (d.error) { setErr(d.error); return; } setData(d); })
      .catch((e) => setErr(e.message));
  }, [ctx.from, ctx.to, ctx.basis]);

  if (err)   return <ErrMsg msg={err} />;
  if (!data) return <Loader />;

  return (
    <div>
      <SectionHeading>Time-to-Cash ({ctx.from} – {ctx.to})</SectionHeading>
      <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>
        Days between invoice issued date and payment received. Only counts settled invoices
        (where a payment record exists in this period).
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard label="Average"       value={data.avgDays    != null ? `${data.avgDays}d`    : "—"} />
        <StatCard label="Median"        value={data.medianDays != null ? `${data.medianDays}d` : "—"} />
        <StatCard label="Fastest"       value={data.minDays    != null ? `${data.minDays}d`    : "—"} color={C.success} />
        <StatCard label="Slowest"       value={data.maxDays    != null ? `${data.maxDays}d`    : "—"} color={data.maxDays > 30 ? C.danger : C.text} />
        <StatCard label="Settled Invoices" value={data.count} />
      </div>
    </div>
  );
}

// ── Main FinanceScreen ────────────────────────────────────────────────────────

const TABS = [
  { id: "overview",     label: "Overview"     },
  { id: "monthly",      label: "Monthly"      },
  { id: "surveys",      label: "Surveys"      },
  { id: "referrals",    label: "Referrals"    },
  { id: "gst",          label: "GST"          },
  { id: "time-to-cash", label: "Time-to-Cash" },
  { id: "expenses",     label: "Expenses"     },
];

function defaultRange() {
  const now   = new Date();
  const from  = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const to    = now.toISOString().slice(0, 10);
  return { from, to };
}

export default function FinanceScreen() {
  const { from: defaultFrom, to: defaultTo } = defaultRange();
  const [tab,   setTab]   = useState("overview");
  const [from,  setFrom]  = useState(defaultFrom);
  const [to,    setTo]    = useState(defaultTo);
  const [basis, setBasis] = useState("cash");

  const ctx = { from, to, basis };

  const inputStyle = {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
    color: C.text, padding: "6px 10px", fontSize: 13,
  };

  const basisBtn = (b) => ({
    padding: "5px 14px", borderRadius: 6, fontSize: 13, cursor: "pointer",
    border: `1px solid ${basis === b ? C.accent : C.border}`,
    background: basis === b ? C.accentDim : "transparent",
    color:       basis === b ? C.accent    : C.muted,
    fontWeight:  basis === b ? 600         : 400,
  });

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1100 }}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div style={{ color: C.text, fontSize: 20, fontWeight: 700 }}>📈 Finance</div>
        <div style={{ flex: 1 }} />

        {/* Date range */}
        <label style={{ color: C.muted, fontSize: 12 }}>From</label>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
        <label style={{ color: C.muted, fontSize: 12 }}>To</label>
        <input type="date" value={to}   onChange={(e) => setTo(e.target.value)}   style={inputStyle} />

        {/* Basis toggle */}
        <button style={basisBtn("cash")}    onClick={() => setBasis("cash")}>Cash</button>
        <button style={basisBtn("accrual")} onClick={() => setBasis("accrual")}>Accrual</button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 20 }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "8px 16px", borderRadius: "6px 6px 0 0", fontSize: 13, cursor: "pointer",
            border: "none", borderBottom: tab === t.id ? `2px solid ${C.accent}` : "2px solid transparent",
            background: tab === t.id ? C.accentDim : "transparent",
            color:      tab === t.id ? C.accent    : C.muted,
            fontWeight: tab === t.id ? 600         : 400,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview"     && <OverviewTab     ctx={ctx} />}
      {tab === "monthly"      && <MonthlyTab      ctx={ctx} />}
      {tab === "surveys"      && <SurveysTab      ctx={ctx} />}
      {tab === "referrals"    && <ReferralsTab    ctx={ctx} />}
      {tab === "gst"          && <GSTTab          ctx={ctx} />}
      {tab === "time-to-cash" && <TimeToCashTab   ctx={ctx} />}
      {tab === "expenses"     && <ExpensesTab     ctx={ctx} />}
    </div>
  );
}
